import React, { useState, useEffect } from 'react';
import { 
  getDashboardAnalytics, 
  getTopEvents, 
  getUserGrowth, 
  getViewsOverTime 
} from '../../lib/supabase';

const AdminAnalyticsPage = () => {
  const [timeRange, setTimeRange] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [topEvents, setTopEvents] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [viewsData, setViewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const [analyticsRes, topEventsRes, userGrowthRes, viewsRes] = await Promise.all([
        getDashboardAnalytics(timeRange),
        getTopEvents(timeRange, 10),
        getUserGrowth(timeRange),
        getViewsOverTime(timeRange)
      ]);

      if (analyticsRes.error) throw analyticsRes.error;
      
      setAnalytics(analyticsRes.data);
      setTopEvents(topEventsRes.data || []);
      setUserGrowth(userGrowthRes.data || []);
      setViewsData(viewsRes.data || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon, color = 'primary' }) => {
    const colorClasses = {
      primary: 'bg-primary/10 text-primary',
      green: 'bg-green-100 text-green-600',
      blue: 'bg-blue-100 text-blue-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
    };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
              {loading ? '...' : (value?.toLocaleString() || '0')}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
      </div>
    );
  };

  const SimpleBarChart = ({ data, dataKey, label, color = '#6366f1' }) => {
    if (!data || data.length === 0) return null;
    
    const maxValue = Math.max(...data.map(d => d[dataKey] || 0), 1);
    
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
        <div className="flex items-end space-x-1 h-32">
          {data.slice(-14).map((item, index) => {
            const height = ((item[dataKey] || 0) / maxValue) * 100;
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full rounded-t transition-all hover:opacity-80"
                  style={{ 
                    height: `${Math.max(height, 2)}%`,
                    backgroundColor: color
                  }}
                  title={`${item.date}: ${item[dataKey] || 0}`}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-400">
          <span>{data.length > 0 ? new Date(data[Math.max(0, data.length - 14)]?.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
          <span>{data.length > 0 ? new Date(data[data.length - 1]?.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400">Track user engagement and platform metrics</p>
        </div>
        
        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Time range:</span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            title="Refresh"
          >
            <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <p className="text-sm text-red-500 mt-1">Make sure to run the analytics migration (009_analytics_tracking.sql) in Supabase.</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={analytics?.total_users}
          subtitle={`+${analytics?.new_users_period || 0} in last ${timeRange} days`}
          color="primary"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
        <StatCard
          title="Total Events"
          value={analytics?.total_events}
          subtitle={`${analytics?.upcoming_events || 0} upcoming`}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          }
        />
        <StatCard
          title="Event Views"
          value={analytics?.total_event_views}
          subtitle={`~${Math.round(analytics?.avg_daily_views || 0)}/day avg`}
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          }
        />
        <StatCard
          title="Total Speakers"
          value={analytics?.total_speakers}
          subtitle={`${analytics?.total_speaker_views || 0} profile views`}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          }
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Growth</h3>
          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <SimpleBarChart 
              data={userGrowth} 
              dataKey="new_users" 
              label="New Users per Day"
              color="#6366f1"
            />
          )}
        </div>

        {/* Views Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Page Views</h3>
          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <SimpleBarChart 
              data={viewsData} 
              dataKey="event_views" 
              label="Event Views per Day"
              color="#10b981"
            />
          )}
        </div>
      </div>

      {/* Top Events Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Viewed Events</h3>
        {loading ? (
          <div className="h-32 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : topEvents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="pb-3 font-medium">Rank</th>
                  <th className="pb-3 font-medium">Event</th>
                  <th className="pb-3 font-medium text-right">Views</th>
                  <th className="pb-3 font-medium text-right">Unique Viewers</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {topEvents.map((event, index) => (
                  <tr key={event.event_id} className="text-sm">
                    <td className="py-3 text-gray-500">#{index + 1}</td>
                    <td className="py-3 font-medium text-gray-900 dark:text-white">
                      {event.event_title || 'Untitled Event'}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {(event.view_count || 0).toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                      {(event.unique_viewers || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>No view data yet</p>
            <p className="text-sm mt-1">Views will appear as users browse events</p>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-primary to-primary-dark rounded-xl p-6 text-white">
          <h4 className="text-sm font-medium opacity-80">Engagement Rate</h4>
          <p className="text-3xl font-bold mt-2">
            {analytics?.total_users > 0 
              ? `${Math.round((analytics?.total_event_views / analytics?.total_users) * 100) / 100}x`
              : '0x'
            }
          </p>
          <p className="text-xs opacity-70 mt-1">Views per user</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <h4 className="text-sm font-medium opacity-80">Growth Rate</h4>
          <p className="text-3xl font-bold mt-2">
            {analytics?.total_users > 0 && analytics?.new_users_period > 0
              ? `+${Math.round((analytics?.new_users_period / analytics?.total_users) * 100)}%`
              : '0%'
            }
          </p>
          <p className="text-xs opacity-70 mt-1">New users in period</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <h4 className="text-sm font-medium opacity-80">Active Events</h4>
          <p className="text-3xl font-bold mt-2">
            {analytics?.upcoming_events || 0}
          </p>
          <p className="text-xs opacity-70 mt-1">Upcoming conferences</p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
